import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { useBankIcons } from './BankContext'
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getPreferredUserIdentifier } from '../utils/userIdentity'
import { handleAuthExpired, handleSessionReplaced } from '../utils/sessionReplacedHandler'

// User Info Types
export interface UserInfoData {
  _id: string
  userId: string
  username: string
  mobile: string
  tenantId: string
  isSystemUser: boolean
  name: string
  email: string
  vendorId: string | null
  parentUserId: string | null
  referrerUsername: string
  siteName: string
  walletBalanceSnapshot: number
  lastIpAddress: string | null
  lastLoginAt: string | null
  lastLogoutAt: string | null
  failedLoginAttempts: number
  lockUntil: string | null
  bankAccount: BankAccount[]
  trueWallet: any[]
  lineAccountId: string
  isAutoDeposit: boolean
  isAutoWithdraw: boolean
  isActiveAccount: boolean
  isEnableChat: boolean
  isShow: boolean
  registerAt: string
  status: 'online' | 'offline'
  bettingLimitGroupId: string | null
  bettingLimitGroupName: string | null
  serviceConfigs: any[]
  limitConfigs: any[]
  promotions: any[]
  bonuses: any[]
  loyaltyPoints?: {
    currentPoints?: number
  }
  flagColor: string
  avatar: string | null
  timezone: string
  language: string
  notes: any[]
  createdAt: string
  updatedAt: string
}

export interface BankAccount {
  bankCode: string
  bankName: string
  accountNumber: string
  accountName: string
  promptPayId?: string
  isActive: boolean
  isDelete: boolean
  createdAt: string
  updatedAt: string
  imageUrl?: string
}

export interface UserInfoResponse {
  success: boolean
  data?: UserInfoData
  error?: string
}

interface UserInfoContextType {
  userInfo: UserInfoData | null
  isLoadingUserInfo: boolean
  refreshUserInfo: () => Promise<void>
  getBankAccount: () => BankAccount | null
  getPrimaryBankAccount: () => BankAccount | null
}

const UserInfoContext = createContext<UserInfoContextType | undefined>(undefined)

interface UserInfoProviderProps {
  children: ReactNode
}

export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const { getBankIcon } = useBankIcons()
  const [userInfo, setUserInfo] = useState<UserInfoData | null>(null)
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(false)

  // Load user info data
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserInfo()
    } else {
      setUserInfo(null)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isAuthenticated) return

    const handleUserServiceAccessUpdated = () => {
      void loadUserInfo()
    }

    window.addEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
    return () => window.removeEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
  }, [isAuthenticated, user])

  const loadUserInfo = async () => {
    try {
      setIsLoadingUserInfo(true)
      
      const user = localStorage.getItem('user')
      if (!user) {
        console.error('❌ No user data found')
        return
      }

      const userData = JSON.parse(user)
      const userId = getPreferredUserIdentifier(userData)
      const tenantId = userData.tenantId

      if (!userId || !tenantId) {
        console.error('❌ Missing userId or tenantId')
        return
      }

      // Get user's access token from localStorage
      const accessToken = localStorage.getItem('accessToken')
      if (!accessToken) {
        console.error('❌ No access token found')
        return
      }

      const response = await fetch(withApiBaseUrl(`/api/v1/user-info/${tenantId}/${userId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-ID': tenantId
        }
      })

      const result: UserInfoResponse = await response.json()
      
      if (response.status === 401 && (result as any)?.code === 'SESSION_REPLACED') {
        await handleSessionReplaced()
        return
      }

      if (response.status === 401 || response.status === 403) {
        await handleAuthExpired()
        return
      }

      if (response.ok && result.success && result.data) {
        setUserInfo(result.data)
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ User info loaded successfully:', result.data)
        }
      } else if (response.status === 404) {
        // User-info record doesn't exist yet - create fallback from user-auth data
        // This is normal for users who registered before user-info service was implemented
        if (process.env.NODE_ENV === 'development') {
          console.log('ℹ️ User-info record not found, using fallback data from user-auth (this is normal for legacy users)')
        }
        
        // Create fallback user info from user-auth data
        const fallbackUserInfo: UserInfoData = {
          _id: userId,
          userId: userId,
          username: userData.username || '',
          mobile: userData.mobile || userData.phoneNumber || '',
          tenantId: tenantId,
          isSystemUser: false,
          name: userData.fullName || userData.displayName || userData.username || '',
          email: userData.email || `${userData.username}@system.local`,
          vendorId: null,
          parentUserId: null,
          referrerUsername: 'system',
          siteName: 'main',
          walletBalanceSnapshot: 0,
          lastIpAddress: null,
          lastLoginAt: userData.lastLogin ? new Date(userData.lastLogin).toISOString() : null,
          lastLogoutAt: null,
          failedLoginAttempts: 0,
          lockUntil: null,
          bankAccount: [],
          trueWallet: [],
          lineAccountId: 'system_default',
          isAutoDeposit: true,
          isAutoWithdraw: true,
          isActiveAccount: userData.status === 'active',
          isEnableChat: false,
          isShow: true,
          registerAt: userData.createdAt || new Date().toISOString(),
          status: 'offline',
          bettingLimitGroupId: null,
          bettingLimitGroupName: null,
          serviceConfigs: [],
          limitConfigs: [],
          promotions: [],
          bonuses: [],
          flagColor: 'none',
          avatar: null,
          timezone: 'Asia/Bangkok',
          language: 'th',
          notes: [],
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        setUserInfo(fallbackUserInfo)
        
        // Reduced logging - only log in development if needed
        if (process.env.NODE_ENV === 'development' && false) { // Disabled to reduce console noise
          console.log('✅ Using fallback user info:', fallbackUserInfo)
        }
      } else {
        // Only log errors that are not 404 (404 is handled above)
        if (response.status !== 404) {
          console.error('❌ Failed to load user info:', result.error || `HTTP ${response.status}`)
        }
        // Still create fallback to prevent app crash
        const fallbackUserInfo: UserInfoData = {
          _id: userId,
          userId: userId,
          username: userData.username || '',
          mobile: userData.mobile || userData.phoneNumber || '',
          tenantId: tenantId,
          isSystemUser: false,
          name: userData.fullName || userData.displayName || userData.username || '',
          email: userData.email || `${userData.username}@system.local`,
          vendorId: null,
          parentUserId: null,
          referrerUsername: 'system',
          siteName: 'main',
          walletBalanceSnapshot: 0,
          lastIpAddress: null,
          lastLoginAt: userData.lastLogin ? new Date(userData.lastLogin).toISOString() : null,
          lastLogoutAt: null,
          failedLoginAttempts: 0,
          lockUntil: null,
          bankAccount: [],
          trueWallet: [],
          lineAccountId: 'system_default',
          isAutoDeposit: true,
          isAutoWithdraw: true,
          isActiveAccount: userData.status === 'active',
          isEnableChat: false,
          isShow: true,
          registerAt: userData.createdAt || new Date().toISOString(),
          status: 'offline',
          bettingLimitGroupId: null,
          bettingLimitGroupName: null,
          serviceConfigs: [],
          limitConfigs: [],
          promotions: [],
          bonuses: [],
          flagColor: 'none',
          avatar: null,
          timezone: 'Asia/Bangkok',
          language: 'th',
          notes: [],
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        setUserInfo(fallbackUserInfo)
      }
    } catch (error) {
      console.error('❌ User info loading error:', error)
      
      // Create fallback even on error to prevent app crash
      try {
        const user = localStorage.getItem('user')
        if (user) {
          const userData = JSON.parse(user)
          const userId = getPreferredUserIdentifier(userData)
          const tenantId = userData.tenantId
          
          if (userId && tenantId) {
            const fallbackUserInfo: UserInfoData = {
              _id: userId,
              userId: userId,
              username: userData.username || '',
              mobile: userData.mobile || userData.phoneNumber || '',
              tenantId: tenantId,
              isSystemUser: false,
              name: userData.fullName || userData.displayName || userData.username || '',
              email: userData.email || `${userData.username}@system.local`,
              vendorId: null,
              parentUserId: null,
              referrerUsername: 'system',
              siteName: 'main',
              walletBalanceSnapshot: 0,
              lastIpAddress: null,
              lastLoginAt: userData.lastLogin ? new Date(userData.lastLogin).toISOString() : null,
              lastLogoutAt: null,
              failedLoginAttempts: 0,
              lockUntil: null,
              bankAccount: [],
              trueWallet: [],
              lineAccountId: 'system_default',
              isAutoDeposit: true,
              isAutoWithdraw: true,
              isActiveAccount: userData.status === 'active',
              isEnableChat: false,
              isShow: true,
              registerAt: userData.createdAt || new Date().toISOString(),
              status: 'offline',
              bettingLimitGroupId: null,
              bettingLimitGroupName: null,
              serviceConfigs: [],
              limitConfigs: [],
              promotions: [],
              bonuses: [],
              flagColor: 'none',
              avatar: null,
              timezone: 'Asia/Bangkok',
              language: 'th',
              notes: [],
              createdAt: userData.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
            setUserInfo(fallbackUserInfo)
            // Reduced logging - only log actual errors, not expected 404s
            if (process.env.NODE_ENV === 'development' && false) { // Disabled to reduce console noise
              console.warn('⚠️ Using fallback user info due to error')
            }
          }
        }
      } catch (fallbackError) {
        console.error('❌ Failed to create fallback user info:', fallbackError)
      }
    } finally {
      setIsLoadingUserInfo(false)
    }
  }

  const refreshUserInfo = async () => {
    await loadUserInfo()
  }

  const getBankAccount = (): BankAccount | null => {
    if (!userInfo || !userInfo.bankAccount || userInfo.bankAccount.length === 0) {
      return null
    }
    
    const bankAccount = userInfo.bankAccount[0]
    
    // Enhance with bank icon
    if (bankAccount) {
      const bankIcon = getBankIcon(bankAccount.bankCode)
      return {
        ...bankAccount,
        imageUrl: bankIcon?.imageUrl || ''
      }
    }
    
    return bankAccount
  }

  const getPrimaryBankAccount = (): BankAccount | null => {
    if (!userInfo || !userInfo.bankAccount || userInfo.bankAccount.length === 0) {
      return null
    }
    
    // Find active bank account
    const activeAccount = userInfo.bankAccount.find(account => 
      account.isActive && !account.isDelete
    )
    
    const primaryAccount = activeAccount || userInfo.bankAccount[0]
    
    // Enhance with bank icon
    if (primaryAccount) {
      const bankIcon = getBankIcon(primaryAccount.bankCode)
      return {
        ...primaryAccount,
        imageUrl: bankIcon?.imageUrl || ''
      }
    }
    
    return primaryAccount
  }

  const value: UserInfoContextType = {
    userInfo,
    isLoadingUserInfo,
    refreshUserInfo,
    getBankAccount,
    getPrimaryBankAccount
  }

  return (
    <UserInfoContext.Provider value={value}>
      {children}
    </UserInfoContext.Provider>
  )
}

export const useUserInfo = (): UserInfoContextType => {
  const context = useContext(UserInfoContext)
  if (context === undefined) {
    throw new Error('useUserInfo must be used within a UserInfoProvider')
  }
  return context
}
